import { useState } from "react";
import {
  validateFixtureLoaderDepth,
  type DividerRuleError,
} from "@CommonUtils/pog/dividerPolicyValidation";
import { EditableField } from "src/components/EditableField/EditableField";
import { Color, Flex } from "src/elements";
import { useTranslation } from "src/i18n";
import { translate } from "src/i18n/translate";
import { displayLength, formatLength, formatLengthError } from "src/utils/lengthUnit";

/** Saves a row's loader depth (x100, null = not set); resolves to an error to show, or null. */
export type SaveLoaderDepth = (loaderDepth: number | null) => Promise<string | null>;

export const loaderDepthTooltip = () => translate("common", "shelfLoader.fixtures.tooltip");

/** The planner's word for a set depth, on the cell: a PSA stores the loader as a negative back overhang. */
export const loaderBackOverhangTooltip = (value: number | null, unit: string) =>
  value !== null && value > 0
    ? translate("common", "shelfLoader.fixtures.backOverhang", {
        depth: formatLength(value, unit),
      })
    : undefined;

/** Blank: "Not set" (null). 0: "None". Otherwise the depth in the merchant's unit. */
export const loaderDepthText = (value: number | null) =>
  value === null
    ? ""
    : value === 0
      ? translate("common", "shelfLoader.fixtures.none")
      : String(displayLength(value));

/** What the user typed as a stored depth: "" -> null, "None" -> 0, "1.5" -> 150. */
export const parseLoaderDepth = (text: string): number | null | "invalid" => {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const lower = trimmed.toLowerCase();
  if (lower === "none" || lower === translate("common", "shelfLoader.fixtures.none").toLowerCase())
    return 0;
  const number = Number(trimmed);
  return Number.isFinite(number) ? Math.round(number * 100) : "invalid";
};

/** A refused depth in the table's words, with {depth} {unit} in the merchant's unit. */
export const loaderDepthErrorText = (
  error: Pick<DividerRuleError, "code" | "params">,
  unit: string,
) => formatLengthError(error, unit);

/**
 * The Fixtures page's "Spring loader depth" cell (review B.3): the library
 * value is the loader's hand setting. Checked here first with the backend's
 * own rules; a refusal from the PUT shows the same copy.
 */
export const LoaderDepthField = ({
  value,
  shelfDepth,
  unit,
  onSave,
  isDisabled,
}: {
  value: number | null;
  shelfDepth: number;
  unit: string;
  onSave?: SaveLoaderDepth;
  isDisabled?: boolean;
}) => {
  const { t } = useTranslation("common");
  const [error, setError] = useState<string | null>(null);

  const save = async (text: string) => {
    const parsed = parseLoaderDepth(text);
    const refused =
      parsed === "invalid"
        ? loaderDepthErrorText({ code: "LOADER_DEPTH_NEGATIVE" }, unit)
        : (() => {
            const invalid = validateFixtureLoaderDepth(parsed, shelfDepth);
            return invalid ? loaderDepthErrorText(invalid, unit) : null;
          })();
    const failed = refused ?? (parsed === "invalid" ? null : ((await onSave?.(parsed)) ?? null));
    setError(failed);
    // A thrown save shows no saved tick; EditableField swallows it.
    if (failed) throw new Error(failed);
    // Shown at once as a reload would show it: "0" reads "None", "1.50" reads "1.5".
    return parsed === "invalid" ? text : loaderDepthText(parsed);
  };

  return (
    <Flex column gap="2px" title={loaderBackOverhangTooltip(value, unit)}>
      <EditableField<string>
        value={loaderDepthText(value)}
        onSave={save}
        isSavedValueShown
        inputProps={{
          placeholder: t("shelfLoader.fixtures.notSet"),
          "aria-label": t("shelfLoader.fixtures.header"),
          disabled: isDisabled || !onSave,
          size: 6,
        }}
      />
      {error && (
        <span role="alert" style={{ color: Color.red, fontSize: 12 }}>
          {error}
        </span>
      )}
    </Flex>
  );
};
